import Link from 'next/link'

import {
  faArrowLeft,
  faPaperPlane,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { spin } from 'components/common/LoadingSpinner'
import { OpenSeaVerified } from 'components/common/OpenSeaVerified'
import { Anchor, Box, Button, Flex, Text, Tooltip } from 'components/primitives'
import { ToastContext } from 'context/ToastContextProvider'
import { useContext, useState } from 'react'
import fetcher from 'utils/fetcher'
import { DATE_REGEX, timeTill } from 'utils/till'
import { isTokenSoulbound } from 'utils/token'
import PriceDisplayToggle from 'components/common/PriceDisplayToggle'
import { getReservoirChainFromContract } from 'config/routes'
import { getSystemIdSupportedChains } from 'utils/multichain'
import { useMergedTokenData } from 'hooks/useMergedTokenData'
import { verifyPirateCollection } from 'utils/collection'

export default function Breadcrumb({
  router,
  token,
  collection,
  contract,
  id,
  countOwned,
}: any) {
  const { addToast } = useContext(ToastContext)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const is1155 = token?.token?.kind === 'erc1155'
  const is721 = token?.token?.kind === 'erc721'
  const chain = getReservoirChainFromContract(token?.token?.contract || '')

  const isSoulbound = isTokenSoulbound(token)

  const systemId = router.query.systemId.toString().split(':')[0]
  const isPirateCollection = verifyPirateCollection(systemId)
  const chains = getSystemIdSupportedChains(systemId)

  const isOwner = countOwned > 0

  const dynamicTokens = useMergedTokenData(systemId, id, 'dynamic', {})

  return (
    <Flex justify="between" align="center" css={{ mb: '$4' }}>
      <Flex align="center" css={{ mr: '$2', gap: '$2' }}>
        <Link href={`/nft/${systemId}`} legacyBehavior={true}>
          <Anchor
            color="primary"
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: '$2',
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} height={16} />
            <Text css={{ color: 'inherit' }} style="subtitle1" ellipsify>
              {token?.token?.collection?.name}
            </Text>
          </Anchor>
        </Link>
        <OpenSeaVerified
          openseaVerificationStatus={collection?.openseaVerificationStatus}
        />
      </Flex>
      <Flex direction="row" align="center" css={{ gap: '$2' }}>
        <PriceDisplayToggle height="40" />
        <Tooltip
          sideOffset={4}
          content={
            <Text style="body3" css={{ display: 'block' }}>
              {!isOwner
                ? 'You do not own this token'
                : isSoulbound
                ? 'This token is soulbound'
                : 'Transfer'}
            </Text>
          }
        >
          <Link
            href={
              isOwner && !isSoulbound
                ? `/asset/${router.query.systemId.toString()}/transfer`
                : '#'
            }
          >
            <Button
              color="gray3"
              size="xs"
              aria-label="transfer token"
              disabled={!isOwner || isSoulbound}
            >
              <FontAwesomeIcon icon={faPaperPlane} width={16} height={16} />
            </Button>
          </Link>
        </Tooltip>

        <Tooltip
          sideOffset={4}
          content={
            <Text style="body3" css={{ display: 'block' }}>
              Refresh Metadata
            </Text>
          }
        >
          <div>
            <Button
              onClick={async (e) => {
                if (isRefreshing) {
                  e.preventDefault()
                  return
                }
                setIsRefreshing(true)

                try {
                  if (!isPirateCollection) {
                    if (is1155 || is721) {
                      const tokensToRefresh = is1155
                        ? chains.map((currCollection) => {
                            const collectionChainId = currCollection.id
                            const collectionToken = dynamicTokens?.data?.find(
                              (token) =>
                                token?.token?.chainId === collectionChainId
                            )

                            return {
                              token: collectionToken,
                              collection: currCollection,
                            }
                          })
                        : dynamicTokens?.data?.map((token) => {
                            const tokenChainId = token?.token?.chainId
                            const currCollection = chains.find(
                              (chain) => chain.id === tokenChainId
                            )

                            return {
                              token,
                              collection: currCollection,
                            }
                          })

                      // Filter out items without a valid contract
                      const validTokens = tokensToRefresh.filter(
                        (item) => item?.token?.token?.contract
                      )

                      const fetchPromises = validTokens.map(
                        async ({ token, collection }) => {
                          return fetcher(
                            `${process.env.NEXT_PUBLIC_PROXY_URL}${collection?.proxyApi}/tokens/refresh/v2`,
                            undefined,
                            {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                              },
                              body: JSON.stringify({
                                tokens: `${token?.token?.contract}:${id}`,
                              }),
                            }
                          )
                        }
                      )

                      const responses = await Promise.all(fetchPromises)

                      const errorResponse = responses.find(
                        (response) =>
                          response?.data?.results?.[0]?.isError ||
                          (response?.data?.statusCode !== undefined &&
                            response?.data?.statusCode !== 200)
                      )

                      if (errorResponse) {
                        throw (
                          errorResponse?.data?.results?.[0]?.result ??
                          errorResponse?.data
                        )
                      }

                      addToast?.({
                        title: 'Refresh token',
                        description:
                          'Request to refresh this token was accepted.',
                      })
                    }
                  } else {
                    // For mainnet/sepolia L1 Pirate Collection, refresh a single token collection
                    const response = await fetcher(
                      `${process.env.NEXT_PUBLIC_PROXY_URL}${chain?.proxyApi}/tokens/refresh/v2`,
                      undefined,
                      {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ tokens: `${contract}:${id}` }),
                      }
                    )

                    if (
                      response?.data?.results?.[0]?.isError ||
                      (response?.data?.statusCode !== undefined &&
                        response?.data?.statusCode !== 200)
                    ) {
                      throw (
                        response?.data?.results?.[0]?.result ?? response?.data
                      )
                    }

                    addToast?.({
                      title: 'Refresh token',
                      description:
                        'Request to refresh this token was accepted.',
                    })
                  }
                } catch (e: any) {
                  const ratelimit = DATE_REGEX.exec(e)?.[0]

                  addToast?.({
                    title: 'Refresh token failed',
                    description: ratelimit
                      ? `This token was recently refreshed. The next available refresh is ${timeTill(
                          ratelimit
                        )}.`
                      : `This token was recently refreshed. Please try again later.`,
                  })
                  throw e
                } finally {
                  setIsRefreshing(false)
                }
              }}
              disabled={isRefreshing}
              color="gray3"
              size="xs"
              css={{ cursor: isRefreshing ? 'not-allowed' : 'pointer' }}
            >
              <Box
                css={{
                  animation: isRefreshing
                    ? `${spin} 1s cubic-bezier(0.76, 0.35, 0.2, 0.7) infinite`
                    : 'none',
                }}
              >
                <FontAwesomeIcon icon={faRefresh} width={16} height={16} />
              </Box>
            </Button>
          </div>
        </Tooltip>
      </Flex>
    </Flex>
  )
}
